import { useEffect, useState } from 'react';
import { useAuth } from '../AuthModule';

const API = process.env.REACT_APP_API_URL || '';

export default function EmojiGameOptimizerPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disapprove, setDisapprove] = useState(false);
  const [editQuestion, setEditQuestion] = useState('');
  const [editAnswer, setEditAnswer] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/emoji-optimizer/entries`)
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'emojis.json not found' : res.statusText);
        return res.json();
      })
      .then((data) => {
        setEntries(Array.isArray(data.entries) ? data.entries : []);
        setIndex(0);
        setError(null);
      })
      .catch((e) => {
        setError(e.message);
        setEntries([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const entry = entries[index] || null;
  const total = entries.length;

  // When entering "disapprove" mode, init edit fields from current entry
  const startDisapprove = () => {
    if (entry) {
      setEditQuestion(entry.question || '');
      setEditAnswer(Array.isArray(entry.answer) ? entry.answer.join(', ') : String(entry.answer || ''));
      setDisapprove(true);
    }
  };

  const saveAndNext = () => {
    if (!entry || saving) return;
    setSaving(true);
    const question = editQuestion.trim();
    const answer = editAnswer
      .split(/[,;\n]+/)
      .map((a) => a.trim())
      .filter(Boolean);
    fetch(`${API}/api/emoji-optimizer/entry/${encodeURIComponent(entry.id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, answer }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then((data) => {
        if (data.entry) {
          const next = [...entries];
          next[index] = data.entry;
          setEntries(next);
        }
        setDisapprove(false);
        setIndex((i) => Math.min(i + 1, total - 1));
      })
      .catch((e) => setError(e.message))
      .finally(() => setSaving(false));
  };

  const approveAndNext = () => {
    setDisapprove(false);
    setIndex((i) => Math.min(i + 1, total - 1));
  };

  const goPrev = () => {
    setDisapprove(false);
    setIndex((i) => Math.max(0, i - 1));
  };

  if (!user) {
    return (
      <div className="App-block">
        <p>Kirjaudu sisään käyttääksesi emoji-optimointityökalua.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="App-block">
        <p>Ladataan emoji-kysymyksiä…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App-block">
        <p className="error">Virhe: {error}</p>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="App-block">
        <h2>Emoji-pelin optimointi</h2>
        <p>Ei yhtään emoji-kysymystä.</p>
      </div>
    );
  }

  return (
    <div className="App-block">
      <h2>Emoji-pelin optimointi</h2>
      <p>
        Kierros {index + 1} / {total}
      </p>
      <div style={{ marginBottom: '1rem' }}>
        <button type="button" onClick={goPrev} disabled={index === 0}>
          ← Edellinen
        </button>
      </div>

      {!disapprove ? (
        <>
          <div style={{ fontSize: '2rem', margin: '1rem 0' }}>{entry.emojis || '—'}</div>
          <p>
            <strong>Kysymys:</strong> {entry.question || '—'}
          </p>
          <p>
            <strong>Vastaukset:</strong>{' '}
            {Array.isArray(entry.answer) ? entry.answer.join(', ') : String(entry.answer || '—')}
          </p>
          <div style={{ marginTop: '1rem' }}>
            <button type="button" onClick={approveAndNext} className="primary">
              Hyväksy
            </button>{' '}
            <button type="button" onClick={startDisapprove}>
              Hylkää ja muokkaa
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: '2rem', margin: '1rem 0' }}>{entry.emojis || '—'}</div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label>
              Kysymys
              <input
                type="text"
                value={editQuestion}
                onChange={(e) => setEditQuestion(e.target.value)}
                style={{ display: 'block', width: '100%', maxWidth: '400px', marginTop: '0.25rem' }}
              />
            </label>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label>
              Vastaukset (pilkulla tai rivillä erotettuna)
              <textarea
                value={editAnswer}
                onChange={(e) => setEditAnswer(e.target.value)}
                rows={3}
                style={{ display: 'block', width: '100%', maxWidth: '400px', marginTop: '0.25rem' }}
              />
            </label>
          </div>
          <div>
            <button type="button" onClick={saveAndNext} disabled={saving} className="primary">
              {saving ? 'Tallennetaan…' : 'Tallenna ja seuraava'}
            </button>{' '}
            <button type="button" onClick={() => setDisapprove(false)} disabled={saving}>
              Peruuta
            </button>
          </div>
        </>
      )}
    </div>
  );
}
